/**
 * SSE client for POST /api/search/by-query/stream (B-P0-09).
 *
 * Browser's native EventSource does not support POST, only GET. We do
 * a regular `fetch()` POST and parse the SSE wire format ourselves
 * off the response body's ReadableStream. The wire format is dead
 * simple: each event is two named lines separated by a blank line.
 *
 *   event: <name>\n
 *   data: <json blob>\n
 *   \n
 *
 * Lines starting with ":" are comments (keep-alives) we discard.
 *
 * Yields typed events as they arrive. Callers consume via `for await`.
 */

import { searchIdentityHeaders } from "@/lib/anon-session";
import { bearerHeaders } from "@/lib/auth-bearer";
import { proofHeaders } from "@/lib/human-proof";

import type {
  ParsedIntent,
  ProductPageData,
  SearchByQueryRequest,
  SearchResult,
} from "./types";

export type SearchStreamEvent =
  | {
      kind: "intent";
      intent: ParsedIntent;
      /**
       * Canonical /c/<slug> path the api pre-committed at intent time,
       * or null when the intent is not publishable. Client uses this
       * to history.replaceState the URL to /c/<slug> while the search
       * is still streaming (Option B-fast, 2026-06-08).
       */
      slug?: string | null;
      /**
       * Phase 3 (2026-06-08): page kind for the pre-committed slug.
       * "product" -> URL is /c/<slug>; "list" -> URL is /c/list/<slug>.
       * Defaults to "product" if absent (pre-Phase-3 api).
       */
      page_kind?: "product" | "list" | "comparison" | null;
      /**
       * 2026-09-19 one best-price rule: the site-wide layout switch, read
       * from the API (MIO_ONE_CARD_ENABLED) so the live view and the saved
       * page can never disagree. Absent on an older api.
       */
      layout?: "one_card" | "classic" | null;
    }
  | {
      kind: "targets";
      targets: Array<{ retailer_slug: string; product_url: string }>;
    }
  | {
      /**
       * 2026-08-28 progressive-UX: an early product image resolved
       * server-side (manufacturer-image cache) the moment the intent is
       * parsed. UNCHECKED: since B-290 item 3 (2026-09-28) the api no
       * longer sends it and the search screen ignores it (kept parsed so
       * an older api during a deploy is harmless).
       */
      kind: "image";
      image_url: string;
    }
  | {
      /**
       * B-290 item 3 (2026-09-28): a photo the photo check CONFIRMED as the
       * product in a clean photo, sent while the search runs. The only photo
       * the search screen shows before the result.
       */
      kind: "hero_image";
      image_url: string;
    }
  | {
      /**
       * 2026-08-28 progressive-UX: the retailers the fan-out is about to
       * query, known from the retailers table ~seconds after intent -
       * long before the `targets` event (~30s+ on a cold search). Lets
       * the UI show real shop names ("checking JB Hi-Fi...") almost
       * immediately. These shops ARE genuinely queried (site: fan-out),
       * so the display is truthful; the `targets` event remains the
       * authority on which shops get fetched.
       */
      kind: "checking";
      retailers: Array<{ slug: string; name: string }>;
    }
  | {
      kind: "result";
      retailer_slug: string;
      product: ProductPageData;
      latency_ms: number | null;
    }
  | {
      kind: "failure";
      retailer_slug: string;
      reason: string;
      latency_ms: number | null;
    }
  | {
      kind: "complete";
      result: SearchResult | null;
      empty_reason: string | null;
      spent_usd: string;
      cost_ceiling_usd: string;
      /**
       * Canonical /c/<slug> path the publisher will write for this
       * search, or null/absent when the search does not meet publish
       * criteria. Used by ComparisonStream to redirect /compare?q=...
       * to /c/<slug> (Option B, 2026-06-08).
       */
      slug?: string | null;
      /**
       * Phase 3 (2026-06-08): page kind for the canonical slug. Maps
       * to the URL prefix (product -> /c/<slug>, list -> /c/list/<slug>).
       * Defaults to "product" if the api omits the field.
       */
      page_kind?: "product" | "list" | "comparison" | null;
    }
  | {
      /**
       * 2026-09-10 (saved-page guard at the till): the backend found a
       * saved, servable page for this query in the register and spent
       * NOTHING - the stream ends after this one event and the client
       * navigates to the saved page. Freshness is handled server-side
       * (a stale page gets one deduped background refresh).
       */
      kind: "saved_page";
      slug: string;
      page_kind?: "product" | "list" | "comparison" | null;
    }
  | {
      kind: "error";
      error: string;
      [field: string]: unknown;
    }
  // A server keepalive (SSE comment). Carries no data; surfaced only so the
  // consumer's idle watchdog resets - the server sends these during slow
  // phases to signal "still working".
  | { kind: "keepalive" };

export class SearchStreamError extends Error {
  status: number;
  /** Seconds until the server will accept the request again; set from the
   *  Retry-After header on a 429 (2026-09-22). Undefined otherwise. */
  retryAfter?: number;
  constructor(status: number, message: string, retryAfter?: number) {
    super(message);
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

function apiBaseUrl(): string {
  if (typeof window === "undefined") {
    return process.env.API_INTERNAL_URL ?? process.env.API_URL ?? "http://localhost:8000";
  }
  return process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
}

export interface BackgroundSearchBody {
  query: string;
  country_code?: string;
  user_postcode?: string | null;
  /** Optional address an anonymous shopper leaves to be emailed when ready. */
  notify_email?: string | null;
  /**
   * false = "attach my email, I'm still watching" (stores notify_email, does
   * NOT background or fire the finisher, so it never races the live SSE).
   * true (default) = "I'm leaving, finish it in the background".
   */
  background?: boolean;
}

/**
 * Walk-away search (2026-08): convert the in-flight search into a durable job
 * so the backend finisher completes it after the shopper leaves, then notifies
 * them when it's ready.
 *
 * Fire-and-forget by design: it mirrors openSearchStream's identity
 * (x-mio-user-id / x-mio-session headers, NOT a Bearer token) and swallows
 * every error, so a failed background request can never disturb the UI - the
 * backend stuck-search sweep is the safety net if this never lands.
 *
 * `keepalive` lets the request still flush while the tab is closing (a plain
 * fetch would be cancelled mid-teardown). We use a keepalive fetch rather than
 * navigator.sendBeacon precisely because sendBeacon sends a headerless Blob and
 * so cannot carry the identity headers this endpoint needs.
 */
export async function backgroundSearch(
  body: BackgroundSearchBody,
  userId?: string | null,
  opts?: { keepalive?: boolean },
): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    await fetch(`${apiBaseUrl()}/api/search/background`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...searchIdentityHeaders(userId),
        ...(await proofHeaders()),
        ...(await bearerHeaders()),
      },
      body: JSON.stringify({
        query: body.query,
        country_code: body.country_code ?? "AU",
        user_postcode: body.user_postcode ?? null,
        notify_email: body.notify_email ?? null,
        background: body.background ?? true,
      }),
      keepalive: opts?.keepalive ?? false,
      cache: "no-store",
    });
  } catch {
    // Best-effort only: never surface a background-request failure.
  }
}

/**
 * Open POST /api/search/by-query/stream and yield parsed SSE events
 * until the server closes the stream.
 *
 * Throws `SearchStreamError` if the HTTP status is non-2xx (the
 * stream never starts) or if the response body is missing.
 *
 * `signal` is an AbortSignal the caller can use to cancel the
 * stream (e.g. on component unmount). Cancelling triggers the
 * server-side `finally` in `stream_fetches` which cancels any
 * outstanding Bright Data requests.
 */
export async function* openSearchStream(
  req: SearchByQueryRequest,
  signal?: AbortSignal,
  userId?: string | null,
): AsyncGenerator<SearchStreamEvent, void, void> {
  const url = `${apiBaseUrl()}/api/search/by-query/stream`;
  // 2026-06-01: x-turnstile-token header removed with Cloudflare
  // Turnstile end-to-end teardown. SearchSecurityMiddleware no
  // longer requires it.
  // B-098: attach the searcher's identity (Clerk user id when signed in,
  // resolved server-side and passed down + the anonymous browser sticker)
  // so the api can count this fresh search against the per-identity wall.
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    ...searchIdentityHeaders(userId),
    // Phase B: real-browser proof. Absent -> server allows in watch mode, blocks
    // in enforce mode. A no-JS scraper can never produce this.
    ...(await proofHeaders()),
    // 2026-08-22: Clerk Bearer so a signed-in user passes the require-sign-in
    // gate. Absent (signed out) -> server refuses the fresh search (correct).
    ...(await bearerHeaders()),
  };
  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(req),
    signal,
    cache: "no-store",
  });

  if (!response.ok || !response.body) {
    const ra = Number(response.headers.get("Retry-After") ?? "");
    throw new SearchStreamError(
      response.status,
      `POST /api/search/by-query/stream returned ${response.status}`,
      Number.isFinite(ra) && ra > 0 ? ra : undefined,
    );
  }

  const reader = response.body
    .pipeThrough(new TextDecoderStream())
    .getReader();

  // Cross-chunk buffer: SSE events can straddle chunk boundaries.
  let buffer = "";
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += value;
      // Each event is terminated by a blank line ("\n\n"). Split off
      // every complete event, leave the trailing partial in buffer.
      let boundary: number;
      while ((boundary = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseSseEvent(raw);
        if (parsed !== null) {
          yield parsed;
        }
      }
    }
  } finally {
    // Best-effort cancel of the underlying reader. If the consumer
    // exits the for-await early (e.g. AbortController fires) we want
    // to make sure the upstream fetch is also torn down.
    try {
      await reader.cancel();
    } catch {
      // Reader already cancelled; ignore.
    }
  }
}

function parseSseEvent(raw: string): SearchStreamEvent | null {
  // A comment-only block is a server keepalive: surface it as a liveness
  // signal so the consumer's idle watchdog resets (the server sends these
  // during slow phases). A genuinely empty block is dropped.
  if (!raw.trim()) return null;
  if (raw.startsWith(":")) return { kind: "keepalive" };
  let eventName = "";
  let dataLine = "";
  for (const line of raw.split("\n")) {
    if (line.startsWith("event: ")) {
      eventName = line.slice(7).trim();
    } else if (line.startsWith("data: ")) {
      // SSE allows multi-line data via repeated `data:` lines. The
      // streaming endpoint always emits a single line per event, but
      // be defensive in case that changes.
      dataLine += (dataLine ? "\n" : "") + line.slice(6);
    } else if (line.startsWith(":")) {
      // Inline comment; ignore.
      continue;
    }
  }
  if (!eventName || !dataLine) return null;

  let payload: unknown;
  try {
    payload = JSON.parse(dataLine);
  } catch {
    return null;
  }

  switch (eventName) {
    case "intent": {
      // 2026-06-08 (Option B-fast + Phase 3): forward optional slug
      // and page_kind from the intent-event payload so ComparisonStream
      // can history.replaceState the URL to either /c/<slug> (product)
      // or /c/list/<slug> (list) while the search is still streaming.
      const p = payload as ParsedIntent & {
        slug?: string | null;
        page_kind?: "product" | "list" | "comparison" | null;
        layout?: "one_card" | "classic" | null;
      };
      const { slug, page_kind, layout, ...intent } = p;
      return {
        kind: "intent",
        intent: intent as ParsedIntent,
        slug: slug ?? null,
        page_kind: page_kind ?? null,
        layout: layout ?? null,
      };
    }
    case "saved_page": {
      const p = payload as {
        slug?: string | null;
        page_kind?: "product" | "list" | "comparison" | null;
      };
      if (!p.slug) return null;
      return {
        kind: "saved_page",
        slug: p.slug,
        page_kind: p.page_kind ?? "product",
      };
    }
    case "targets":
      return {
        kind: "targets",
        targets: payload as Array<{
          retailer_slug: string;
          product_url: string;
        }>,
      };
    case "image": {
      const p = payload as { image_url?: string | null };
      if (!p.image_url) return null;
      return { kind: "image", image_url: p.image_url };
    }
    case "hero_image": {
      const p = payload as { image_url?: string | null };
      if (!p.image_url) return null;
      return { kind: "hero_image", image_url: p.image_url };
    }
    case "checking": {
      const p = payload as {
        retailers?: Array<{ slug?: string; name?: string }>;
      };
      const retailers = (p.retailers ?? [])
        .filter((r) => !!r.slug && !!r.name)
        .map((r) => ({ slug: r.slug as string, name: r.name as string }));
      if (retailers.length === 0) return null;
      return { kind: "checking", retailers };
    }
    case "result": {
      const p = payload as {
        retailer_slug: string;
        product: ProductPageData;
        latency_ms: number | null;
      };
      return {
        kind: "result",
        retailer_slug: p.retailer_slug,
        product: p.product,
        latency_ms: p.latency_ms,
      };
    }
    case "failure": {
      const p = payload as {
        retailer_slug: string;
        reason: string;
        latency_ms: number | null;
      };
      return {
        kind: "failure",
        retailer_slug: p.retailer_slug,
        reason: p.reason,
        latency_ms: p.latency_ms,
      };
    }
    case "complete": {
      // 2026-06-08: surface `slug` + `page_kind` from the wire payload.
      // Phase 3 added page_kind so the client can route to /c/<slug>
      // (product) vs /c/list/<slug> (list). Pre-Phase-3 api omits
      // page_kind, in which case we default to "product".
      const p = payload as {
        result: SearchResult | null;
        empty_reason: string | null;
        spent_usd: string;
        cost_ceiling_usd: string;
        slug?: string | null;
        page_kind?: "product" | "list" | "comparison" | null;
      };
      return {
        kind: "complete",
        result: p.result,
        empty_reason: p.empty_reason,
        spent_usd: p.spent_usd,
        cost_ceiling_usd: p.cost_ceiling_usd,
        slug: p.slug ?? null,
        page_kind: p.page_kind ?? null,
      };
    }
    case "error":
      return { kind: "error", ...(payload as { error: string }) };
    default:
      return null;
  }
}
